<template>
  <div ref="layoutViewRef" id="tester-portal">
    <AntSpin :spinning="spinVisible">
      <Layout
        v-if="loginUserData.userId"
        :loginUserData="loginUserData"
        :permissionData="permissionData"
        :userLevelList="userLevelList"
        :sourceLineList="sourceLineList"
        :selectedUserLevel="selectedUserLevel"
        :selectedSourceLine="selectedSourceLine"
        :loginPlayerData="loginPlayerData"
        :requestResponseData="requestResponseData"
        :onChangeSelectedUserLevel="onChangeSelectedUserLevel"
        :onChangeSelectedSourceLine="onChangeSelectedSourceLine"
      >
        <RouterView
          :loginUserData="loginUserData"
          :permissionData="permissionData"
          :selectedUserLevel="selectedUserLevel"
          :selectedSourceLine="selectedSourceLine"
          :loginPlayerData="loginPlayerData"
          :ratioData="ratioData"
          :fetchAwcApi="fetchAwcApi"
          :onChangeSpinVisible="onChangeSpinVisible"
          :onChangeLoginUserData="onChangeLoginUserData"
          :onChangeLoginPlayerData="onChangeLoginPlayerData"
          :onChangePermissionData="onChangePermissionData"
        />
      </Layout>
      <RouterView
        v-else
        :loginUserData="loginUserData"
        :onChangeLoginUserData="onChangeLoginUserData"
      />
    </AntSpin>
  </div>
</template>

<script>
import {
  ref,
  reactive,
  watch,
  toRaw,
  onMounted,
} from 'vue'

import { useRouter, useRoute } from 'vue-router'
import { jwtDecode } from 'jwt-decode'
import moment from 'moment'
import axios from 'axios'

import {
  Spin as AntSpin,
  message as antMessage,
} from 'ant-design-vue'

import Layout from '@/components/TesterPortal/Layout'

import { refreshAuthToken } from '@/api/testerportal/login'
import { getPermissions, getSourceLines, getCurrenciesBySourceAgent } from '@/api/testerportal'

import { AWC_ENV_LIST } from '@/constants/index'
import { USER_LEVEL_LIST_BY_PERMISSION } from '../constants/testerPortal'

export default {
  name: 'TestPortal',
  components: {
    Layout,
    AntSpin,
  },
  setup() {
    const emptyObject = obj => Object.keys(obj).forEach(key => delete obj[key])

    const router = useRouter()
    const route = useRoute()
    const layoutViewRef = ref()

    const selectedUserLevel = ref('')
    const userLevelList = ref([])

    const selectedSourceLine = reactive({})
    const sourceLineList = reactive([])

    const loginUserData = reactive({})
    const loginPlayerData = reactive({})
    const ratioData = ref([])
    const permissionData = reactive({})

    const spinVisible = ref(false)
    const spinKeyList = ref([])

    const onChangeSpinVisible = (val, key) => {
      // console.log('onChangeSpinVisible', val, key)
      if (!val) {
        spinKeyList.value = spinKeyList.value.filter(d => d !== key)
      } else {
        spinKeyList.value.push(key)
      }

      spinVisible.value = !!spinKeyList.value.length
    }

    const token = localStorage.getItem('token')
    let decodedToken = {}
    if (token) {
      const now = moment().unix()
      decodedToken = jwtDecode(token)
      const isTokenExpired = decodedToken.exp <= now
      const lastActivity = localStorage.getItem('lastActivity')
      const isIdle = !lastActivity || (now - lastActivity > 30 * 60)
      decodedToken = isIdle || isTokenExpired ? {} : decodedToken
      // console.log('decodedToken', decodedToken, isIdle)
      Object.assign(loginUserData, decodedToken)
    }

    const requestResponseData = reactive({
      url: '',
      request: '',
      response: '',
    })

    const getCurrencyAndRatio = async () => {
      console.log('getCurrencyAndRatio agentId', selectedSourceLine.agentId)

      const reqData = sourceLineList.reduce((acc, { source, agentId }) => {
        if (!acc[source]) acc[source] = []
        acc[source].push(agentId)
        return acc
      }, {})

      const { data, ...currencyDataBySourceAgent } = await getCurrenciesBySourceAgent(reqData)

      sourceLineList.forEach(({ source, agentId }, idx) => {
        const currency = currencyDataBySourceAgent[source]?.[agentId] || []
        sourceLineList[idx].currency = currency
        if (selectedSourceLine.source === source && selectedSourceLine.agentId === agentId) {
          selectedSourceLine.currency = currency
        }
      })

      ratioData.value.length = 0
      ratioData.value.push(...data.map((d) => {
        let equivalentInTHB
        if (d.realCurrency) {
          const ratioArr = d.ratio.split(':')
          const computredRatio = ratioArr[0] / ratioArr[1]
          equivalentInTHB = Number((computredRatio * d.rate * 1000).toFixed(3))
        }
        return {
          ...d,
          equivalentInTHB,
        }
      }))
    }

    const fetchAwcApi = async ({
      api, requestData: reqData, showMessage, onSuccess, onFail,
    }) => {
      const messageKey = `${api}-${Date.now()}`
      if (showMessage) antMessage.loading('', { key: messageKey })

      const { agentId, cert } = selectedSourceLine
      const requestData = {
        agentId,
        cert,
        ...reqData,
      }

      let source = selectedSourceLine.source.toUpperCase()
      if (source === 'NGIUAT' && selectedSourceLine.uatDomain) {
        source = `UAT_${selectedSourceLine.uatDomain}`
      }

      const url = `${AWC_ENV_LIST[source].domain}/wallet/${api}`
      const { data: { data: awcData, error } } = await axios.post(`/proxy/${url}`, requestData)

      requestResponseData.url = url
      requestResponseData.request = JSON.stringify(requestData)
      requestResponseData.response = JSON.stringify(awcData)

      if (awcData) {
        if (awcData.status === '0000') {
          if (showMessage) {
            antMessage.success({
              key: messageKey,
              content: `Player ID: ${requestData.userId || requestData.userIds} ${api} successfully`,
            })
          }
          if (onSuccess) onSuccess(awcData)
        } else {
          if (showMessage) {
            antMessage.error({
              key: messageKey,
              duration: 5,
              content: `Failed to ${api} [${requestData.userId || requestData.userIds}]: ${requestResponseData.response}`,
            })
          }
          if (onFail) onFail(awcData)
        }
      } else if (showMessage) {
        antMessage.error({
          key: messageKey,
          duration: 5,
          content: `Failed to ${api} [${requestData.userId || requestData.userIds}]: ${error.message}`,
        })
      }

      return awcData
    }

    const onChangeSelectedSourceLine = (val) => {
      console.log('onChangeSelectedSourceLine', val)
      if (!val) {
        emptyObject(selectedSourceLine)
        return
      }

      let sourceLineData = val
      if (typeof val === 'string') { // used in Layout
        sourceLineData = sourceLineList.find(({ sourceLineId }) => sourceLineId === val)
      }
      console.log('onChangeSelectedSourceLine sourceLineData', sourceLineData)
      Object.assign(selectedSourceLine, sourceLineData)
    }

    const onChangeSelectedUserLevel = async (userLevel) => {
      console.log('onChangeSelectedUserLevel', userLevel)
      if (!userLevel) return

      selectedUserLevel.value = userLevel
      let { data: sourceLinesData } = await getSourceLines(userLevel)
      console.log('getSourceLines data', sourceLinesData)

      if (sourceLinesData) {
        onChangeSpinVisible(true, 'changeSelectedUserLevel')

        sourceLinesData = sourceLinesData.sort((a, b) => {
          if (a.sourceLineId.startsWith('uat') && !b.sourceLineId.startsWith('uat')) return -1
          if (!a.sourceLineId.startsWith('uat') && b.sourceLineId.startsWith('uat')) return 1
          return a.sourceLineId.localeCompare(b.sourceLineId)
        })

        sourceLineList.length = 0
        sourceLineList.push(...sourceLinesData)
        await onChangeSelectedSourceLine()
        await getCurrencyAndRatio()

        onChangeSpinVisible(false, 'changeSelectedUserLevel')
      }
    }

    const onChangePermissionData = (data) => {
      console.log('onChangePermissionData', data)
      Object.assign(permissionData, data)
    }

    const onChangeLoginUserData = (data) => {
      console.log('onChangeLoginUserData', data)
      if (data) {
        Object.assign(loginUserData, data)
      } else {
        emptyObject(loginUserData)
      }
    }

    const onChangeLoginPlayerData = (data) => {
      console.log('onChangeLoginPlayerData', data)
      if (data) {
        Object.assign(loginPlayerData, data)
      } else {
        emptyObject(loginPlayerData)
      }
    }

    watch(loginUserData, async (newVal, oldVal) => {
      console.log('watch loginUserData', toRaw(newVal), toRaw(oldVal), selectedUserLevel.value)
      if (!newVal.userId) selectedUserLevel.value = undefined
      if (!selectedUserLevel.value && newVal.userId) {
        if (!Object.keys(permissionData).length) {
          const { data } = await getPermissions()
          const permissionDataMap = data.reduce((acc, { permission, allowedUserLevels }) => {
            acc[permission] = allowedUserLevels
            return acc
          }, {})

          onChangePermissionData(permissionDataMap)
          console.log('route.name', route.name)
          console.log('permissionDataMap', permissionDataMap)
          if (!['TesterPortalHome', 'TesterPortalLogin'].includes(route.name)
            && !permissionDataMap[`${route.name}_View`].includes(newVal.permission)) {
            router.replace('/testerportal')
          }
        }

        userLevelList.value = USER_LEVEL_LIST_BY_PERMISSION[newVal.permission]
        selectedUserLevel.value = userLevelList.value[0] // eslint-disable-line prefer-destructuring
        await onChangeSelectedUserLevel(selectedUserLevel.value)
      }
    }, { immediate: true })

    onMounted(async () => {
      layoutViewRef.value.addEventListener('click', async () => {
        const now = moment().unix()
        const lastActivity = localStorage.getItem('lastActivity')

        // console.log('--- CLICK ---',
        //   'loginUserData.exp:', loginUserData.exp ? moment(loginUserData.exp * 1000).format() : loginUserData.exp, `(${loginUserData.exp})`,
        //   'lastActivity:', lastActivity ? moment(lastActivity * 1000).format() : null, `(${lastActivity})`,
        //   'Now:', moment(now * 1000).format(), `(${now})`)

        if (loginUserData.exp) {
          const isIdle = lastActivity && (now - lastActivity > 30 * 60)
          if (isIdle) {
            const isTokenExpired = loginUserData.exp <= now
            const isTokenAlmostExpired = loginUserData.exp - now < 30 * 60
            let data
            if (!isTokenExpired && isTokenAlmostExpired) ({ data } = await refreshAuthToken())
            onChangeLoginUserData(data)
            // console.log(1111, 'isIdle')
            localStorage.removeItem('lastActivity')
            alert('You have been logged out of your account') // eslint-disable-line no-alert
            router.push('/testerportal/login')
          } else { // lastActivity is in 30m or no lastActivity
            // console.log(2222, 'lastActivity is in 30m or no lastActivity')
            localStorage.setItem('lastActivity', now)
          }
        }
      })
    })

    return {
      selectedUserLevel,
      userLevelList,
      selectedSourceLine,
      sourceLineList,
      layoutViewRef,
      loginUserData,
      loginPlayerData,
      requestResponseData,
      ratioData,
      permissionData,
      fetchAwcApi,
      onChangeLoginUserData,
      onChangeSelectedUserLevel,
      onChangeSelectedSourceLine,
      onChangeLoginPlayerData,
      onChangePermissionData,
      spinVisible,
      onChangeSpinVisible,
    }
  },
}
</script>

<style>
#tester-portal .ant-table-thead > tr > th, #tester-portal .ant-table tfoot > tr > th {
  overflow-wrap: normal;
}

.ant-table-cell .ant-divider-vertical {
  display: none;
}

.ant-table-cell a + .ant-divider-vertical:has(+ a), .ant-table-cell a + .ant-divider-vertical:has(+ span), .ant-table-cell span + .ant-divider-vertical:has(+ a), .ant-table-cell span + .ant-divider-vertical:has(+ span) {
  display: inline-block;
}
</style>
